import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";
import {
  CLIENT_APP_WORKSPACE_LOTS,
  CLIENT_APP_WORKSPACE_SECTIONS,
  applyClientAppWorkspace,
  mealPrepNotApplied,
  buildClientAppWorkspaceContext,
  buildClientAppWorkspaceLotPrompt,
  createClientAppWorkspaceDraft,
  editClientAppWorkspaceLotContent,
  LOT_1_LIMITS,
  importClientAppWorkspaceLot,
  markClientAppWorkspaceSectionReviewed,
  persistClientAppWorkspaceDraft,
  updateClientAppWorkspaceRawDraft,
  MEALS_AUTHORITY_SOURCES,
  chooseClientAppWorkspaceMealsSource,
  getWeekEditorApplyIssues,
  hasWeekEditorMeals,
  requiredClientAppWorkspaceLots,
  looksLikePastedFrame,
  PASTED_FRAME_MESSAGE,
  getClientAppWorkspaceLotIssueDetail,
} from "../services/clientAppWorkspace";
import { buildWeekMenusModel } from "../services/weekMenus";
import {
  AUTO_LOT_IDS,
  LOT_3_DEFAULTS,
  buildLot3RawText,
  buildLot4RawText,
  getDossierAutoGenerationBlocker,
  getLot2AutoGenerationBlocker,
  prepareClientAppDossierAuto,
  readDossierAutoMgdState,
  readDossierAutoFreshFromCloud,
  MESSAGE_LOT_2_COMPLETE,
  abortDossierAutoRun,
  isDossierAutoRunning,
  startDossierAutoRun,
  MESSAGE_AUTO_ALREADY_RUNNING,
} from "../services/clientAppWorkspaceAuto";

const DAY_OPTIONS = Object.freeze([
  ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"], ["thursday", "Jeudi"],
  ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
]);

const AUTO_STATUS_LABELS = Object.freeze({
  running: "Rédaction en cours…",
  generated: "Proposition reçue, vérification…",
  done: "Prêt à relire",
  error: "À relancer",
});

/** Lot 3 sans IA : réglages du Meal Prep pré-remplis, Anissa confirme. */
function Lot3Form({ onSubmit, disabled }) {
  const [settings, setSettings] = useState(() => ({ ...LOT_3_DEFAULTS, preferred_days: [...LOT_3_DEFAULTS.preferred_days] }));
  const setSessionCount = (count) => setSettings((current) => {
    const days = current.preferred_days.slice(0, count);
    for (const [day] of [...DAY_OPTIONS].reverse()) {
      if (days.length >= count) break;
      if (!days.includes(day)) days.push(day);
    }
    return { ...current, session_count: count, preferred_days: days };
  });
  const daysValid = new Set(settings.preferred_days).size === settings.preferred_days.length;
  return (
    <div className="client-app-editor">
      <p className="client-app-editor__intro">Réglages pré-remplis. Ajuste si besoin puis enregistre. La rotation des 4 semaines est calculée à partir des menus.</p>
      <label className="client-app-editor__field">
        <span>Nombre de personnes</span>
        <input type="number" min={1} max={20} value={settings.people} onChange={(event) => setSettings((current) => ({ ...current, people: Math.min(20, Math.max(1, Number.parseInt(event.target.value, 10) || 1)) }))} />
      </label>
      <label className="client-app-editor__field">
        <span>Sessions de préparation par semaine</span>
        <select value={settings.session_count} onChange={(event) => setSessionCount(Number(event.target.value))}>
          {[1, 2, 3].map((count) => <option key={count} value={count}>{count}</option>)}
        </select>
      </label>
      {settings.preferred_days.map((day, index) => (
        <label className="client-app-editor__field" key={`session-${index}`}>
          <span>Jour de la session {index + 1}</span>
          <select value={day} onChange={(event) => setSettings((current) => ({ ...current, preferred_days: current.preferred_days.map((item, position) => (position === index ? event.target.value : item)) }))}>
            {DAY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      ))}
      {!daysValid && <p className="client-app-workspace__inline-error">Choisis un jour différent pour chaque session.</p>}
      <div className="client-app-editor__actions">
        <button type="button" className="client-app-editor__save" onClick={() => onSubmit(settings)} disabled={disabled || !daysValid}>
          Enregistrer ces réglages
        </button>
      </div>
    </div>
  );
}

const LOT_HELP = Object.freeze({
  lot_1: "Message d’accueil, repères essentiels et piliers du programme.",
  lot_2: "Semaine complète, menus variés, alternatives et recettes chiffrées.",
  lot_3: "Quatre semaines réellement différentes et organisation du Meal Prep.",
  lot_4: "Repères du frigo et présentation du protocole déjà validé.",
});

const WEEK_EDITOR_SOURCE_LINE = "Source : menus modifiés dans l’éditeur";

/** Ce qui sera réellement appliqué quand les menus viennent de l'éditeur Semaine. */
function WeekEditorPreview({ weekMenus, part }) {
  if (!weekMenus) return null;
  const choiceTitle = (slotModel, id) => slotModel.choices.find((choice) => choice.id === id)?.title || "À choisir";
  return (
    <div className="client-app-workspace__lot-preview client-app-workspace__week-editor-preview">
      <h5>{part === "rotation" ? "Rotation des 4 semaines appliquée" : "Menus et recettes appliqués"}</h5>
      <p><strong>{WEEK_EDITOR_SOURCE_LINE}</strong></p>
      {part === "rotation" ? (
        <>
          <p>{weekMenus.rotation.statusLabel}{weekMenus.rotation.message ? ` · ${weekMenus.rotation.message}` : ""}</p>
          {weekMenus.weeks.map((week) => (
            <details key={week.index}>
              <summary>{week.label}</summary>
              {weekMenus.slots.map((slotModel) => (
                <p key={slotModel.slot}><strong>{slotModel.label}</strong> · {choiceTitle(slotModel, week.mainBySlot[slotModel.slot])}</p>
              ))}
            </details>
          ))}
        </>
      ) : (
        weekMenus.slots.map((slotModel) => (
          <details key={slotModel.slot}>
            <summary>{slotModel.label} · {slotModel.choices.length - 1} alternatives · {slotModel.statusLabel}</summary>
            {slotModel.choices.map((choice) => (
              <div key={choice.id} className="client-app-workspace__recipe-line">
                <strong>{choice.title}</strong>
                {choice.hint && <span>{choice.hint}</span>}
                <small>{(choice.recipe?.ingredients || []).join(" · ") || "Recette à écrire"}</small>
              </div>
            ))}
          </details>
        ))
      )}
      {weekMenus.issues.length > 0 && (
        <ul>{weekMenus.issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
      )}
    </div>
  );
}

function weekEditorSectionPreview(sectionId, weekMenus) {
  if (sectionId === "week") {
    const total = weekMenus.slots.reduce((sum, slotModel) => sum + slotModel.choices.length, 0);
    return `${WEEK_EDITOR_SOURCE_LINE} · ${weekMenus.slots.length} repas · ${total} choix · ${weekMenus.statusLabel}`;
  }
  if (sectionId === "rotation") {
    return `${WEEK_EDITOR_SOURCE_LINE} · ${weekMenus.rotation.statusLabel}`;
  }
  return null;
}

function sectionPreview(sectionId, workspace, weekMenus = null) {
  if (weekMenus) {
    const preview = weekEditorSectionPreview(sectionId, weekMenus);
    if (preview) return preview;
  }
  const lot1 = workspace?.lots?.lot_1?.content;
  const lot2 = workspace?.lots?.lot_2?.content;
  const lot3 = workspace?.lots?.lot_3?.content;
  const lot4 = workspace?.lots?.lot_4?.content;
  if (sectionId === "introduction") {
    return lot1?.introduction ? `${lot1.introduction.greeting} · ${lot1.introduction.body.length} paragraphe(s)` : "Lot non importé";
  }
  if (sectionId === "strategy") {
    return lot1?.strategy ? `${lot1.strategy.pillars.length} pilier(s) · ${lot1.strategy.takeaways.length} repère(s)` : "Lot non importé";
  }
  if (sectionId === "week") {
    const recipes = lot2?.meals?.reduce((total, meal) => total + 1 + (meal.alternatives?.length || 0), 0) || 0;
    return lot2 ? `${lot2.meals.length} moment(s) · ${recipes} recettes chiffrées` : "Lot non importé";
  }
  if (sectionId === "rotation") return lot3 ? "4 semaines distinctes calculées à partir des menus" : "Lot non importé";
  if (sectionId === "meal_prep") {
    return lot3 ? `${lot3.meal_prep.people} personne(s) · ${lot3.meal_prep.session_count} session(s)` : "Lot non importé";
  }
  if (sectionId === "fridge") {
    return lot4 ? `${lot4.fridge.to_favor.length} repère(s) à privilégier · ${lot4.fridge.to_limit.length} à limiter` : "Lot non importé";
  }
  if (sectionId === "supplements") return lot4 ? "Présentation liée au protocole validé" : "Lot non importé";
  return "";
}

function RecipeLine({ choice }) {
  const ingredients = Array.isArray(choice?.recipe?.ingredients_structured)
    ? choice.recipe.ingredients_structured
    : [];
  return (
    <div className="client-app-workspace__recipe-line">
      <strong>{choice?.title}</strong>
      {choice?.hint && <span>{choice.hint}</span>}
      <small>{ingredients.map((item) => `${item.quantity ?? ""} ${item.unit} ${item.label}`.trim()).join(" · ")}</small>
    </div>
  );
}

function LotPreview({ lotId, workspace, context, weekMenus = null }) {
  const lot = workspace?.lots?.[lotId];
  // Menus de l'éditeur Semaine : l'aperçu montre ce qui sera appliqué, pas le lot.
  if (weekMenus && lot?.status === "ready" && lotId === "lot_2") {
    return <WeekEditorPreview weekMenus={weekMenus} part="week" />;
  }
  if (weekMenus && lot?.status === "ready" && lotId === "lot_3") {
    const prep = lot.content.meal_prep;
    return (
      <>
        <div className="client-app-workspace__lot-preview">
          <h5>Meal Prep</h5>
          <p>{prep.people} personne(s) · {prep.session_count} session(s) · {prep.preferred_days.join(" + ")}</p>
        </div>
        <WeekEditorPreview weekMenus={weekMenus} part="rotation" />
      </>
    );
  }
  if (lot?.status === "needs_anissa_validation") {
    return (
      <div className="client-app-workspace__lot-preview client-app-workspace__lot-preview--blocked" role="alert">
        <h5>À valider Anissa</h5>
        {lot.issues.map((issue) => <p key={issue}>{issue}</p>)}
      </div>
    );
  }
  const content = lot?.content;
  if (!content) return null;
  if (lotId === "lot_1") {
    return (
      <div className="client-app-workspace__lot-preview">
        <h5>Aperçu du lot importé</h5>
        <details open><summary>Introduction</summary><p><strong>{content.introduction.greeting}</strong></p>{content.introduction.body.map((text) => <p key={text}>{text}</p>)}</details>
        <details><summary>Stratégie</summary>{content.strategy.pillars.map((pillar) => <p key={pillar.title}><strong>{pillar.title}</strong> · {pillar.description}</p>)}</details>
      </div>
    );
  }
  if (lotId === "lot_2") {
    return (
      <div className="client-app-workspace__lot-preview">
        <h5>Aperçu des menus et grammages</h5>
        {content.meals.map((meal) => (
          <details key={meal.slot}><summary>{meal.primary.title} · {meal.alternatives.length} alternatives</summary><RecipeLine choice={meal.primary} />{meal.alternatives.map((choice) => <RecipeLine key={choice.title} choice={choice} />)}</details>
        ))}
      </div>
    );
  }
  if (lotId === "lot_3") {
    const meals = workspace?.lots?.lot_2?.content?.meals || [];
    return (
      <div className="client-app-workspace__lot-preview">
        <h5>Aperçu du cycle et du Meal Prep</h5>
        <p>{content.meal_prep.people} personne(s) · {content.meal_prep.session_count} session(s) · {content.meal_prep.preferred_days.join(" + ")}</p>
        {meals.map((meal) => <details key={meal.slot}><summary>{meal.slot}</summary>{[meal.primary, ...meal.alternatives].slice(0, 4).map((choice, index) => <p key={choice.title}><strong>Semaine {index + 1}</strong> · {choice.title}</p>)}</details>)}
      </div>
    );
  }
  return (
    <div className="client-app-workspace__lot-preview">
      <h5>Aperçu du frigo et des compléments</h5>
      <details open><summary>Frigo</summary><p><strong>À privilégier</strong> · {content.fridge.to_favor.join(" · ")}</p><p><strong>À limiter</strong> · {content.fridge.to_limit.join(" · ") || "Aucun repère ajouté"}</p></details>
      <details><summary>Compléments déjà validés</summary><p>{content.supplements.intro}</p>{context.supplementItems.length > 0 ? context.supplementItems.map((item) => <p key={item.id}><strong>{item.name}</strong>{item.dose ? ` · ${item.dose}` : ""}</p>) : <p>Aucun complément prévu.</p>}</details>
    </div>
  );
}

function StringListEditor({ legend, hint, values, onChange, min, max, maxLength, rows = 2, addLabel }) {
  return (
    <fieldset className="client-app-editor__group">
      <legend>{legend}{hint && <small>{hint}</small>}</legend>
      {values.map((value, index) => (
        <div className="client-app-editor__row" key={`${legend}-${index}`}>
          <textarea
            rows={rows}
            maxLength={maxLength}
            value={value}
            onChange={(event) => onChange(values.map((item, position) => (position === index ? event.target.value : item)))}
          />
          <button
            type="button"
            className="client-app-editor__remove"
            onClick={() => onChange(values.filter((_, position) => position !== index))}
            disabled={values.length <= min}
          >
            Retirer
          </button>
        </div>
      ))}
      {values.length === 0 && <p className="client-app-editor__empty">Aucune entrée. Cette section restera vide dans l’application.</p>}
      {values.length < max && (
        <button type="button" className="client-app-editor__add" onClick={() => onChange([...values, ""])}>{addLabel}</button>
      )}
    </fieldset>
  );
}

function PairListEditor({ legend, hint, values, onChange, min, max, fields, addLabel }) {
  return (
    <fieldset className="client-app-editor__group">
      <legend>{legend}{hint && <small>{hint}</small>}</legend>
      {values.map((value, index) => (
        <div className="client-app-editor__pair" key={`${legend}-${index}`}>
          <input
            type="text"
            maxLength={fields.primary.maxLength}
            placeholder={fields.primary.label}
            value={value[fields.primary.key] || ""}
            onChange={(event) => onChange(values.map((item, position) => (
              position === index ? { ...item, [fields.primary.key]: event.target.value } : item
            )))}
          />
          <textarea
            rows={2}
            maxLength={fields.secondary.maxLength}
            placeholder={fields.secondary.label}
            value={value[fields.secondary.key] || ""}
            onChange={(event) => onChange(values.map((item, position) => (
              position === index ? { ...item, [fields.secondary.key]: event.target.value } : item
            )))}
          />
          <button
            type="button"
            className="client-app-editor__remove"
            onClick={() => onChange(values.filter((_, position) => position !== index))}
            disabled={values.length <= min}
          >
            Retirer
          </button>
        </div>
      ))}
      {values.length === 0 && <p className="client-app-editor__empty">Aucune entrée. Cette section restera vide dans l’application.</p>}
      {values.length < max && (
        <button
          type="button"
          className="client-app-editor__add"
          onClick={() => onChange([...values, { [fields.primary.key]: "", [fields.secondary.key]: "" }])}
        >
          {addLabel}
        </button>
      )}
    </fieldset>
  );
}

export function Lot1Editor({ content, onSubmit, disabled, busy }) {
  const [draft, setDraft] = useState(content);
  useEffect(() => { setDraft(content); }, [content]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(content), [draft, content]);
  const patchIntroduction = (patch) => setDraft((current) => ({ ...current, introduction: { ...current.introduction, ...patch } }));
  const patchStrategy = (patch) => setDraft((current) => ({ ...current, strategy: { ...current.strategy, ...patch } }));

  return (
    <div className="client-app-editor">
      <p className="client-app-editor__intro">
        Corrige ce texte comme tu le dirais à ta cliente. Chaque modification redemande une relecture de la section.
      </p>

      <h5>Introduction</h5>
      <label className="client-app-editor__field">
        <span>Salutation</span>
        <input type="text" maxLength={LOT_1_LIMITS.greeting} value={draft.introduction.greeting} onChange={(event) => patchIntroduction({ greeting: event.target.value })} />
      </label>
      <StringListEditor
        legend="Paragraphes"
        hint={`${LOT_1_LIMITS.body.min} à ${LOT_1_LIMITS.body.max} paragraphes, chacun différent. Un paragraphe par entrée, les retours à la ligne ne sont pas conservés.`}
        values={draft.introduction.body}
        onChange={(body) => patchIntroduction({ body })}
        min={LOT_1_LIMITS.body.min}
        max={LOT_1_LIMITS.body.max}
        maxLength={LOT_1_LIMITS.body.maxLength}
        rows={4}
        addLabel="Ajouter un paragraphe"
      />
      <label className="client-app-editor__field">
        <span>Phrase mise en avant <small>facultative</small></span>
        <input type="text" maxLength={LOT_1_LIMITS.pull_quote} value={draft.introduction.pull_quote} onChange={(event) => patchIntroduction({ pull_quote: event.target.value })} />
      </label>
      <PairListEditor
        legend="Points personnalisés"
        hint={`${LOT_1_LIMITS.tailored_points.max} au maximum`}
        values={draft.introduction.tailored_points}
        onChange={(tailored_points) => patchIntroduction({ tailored_points })}
        min={LOT_1_LIMITS.tailored_points.min}
        max={LOT_1_LIMITS.tailored_points.max}
        fields={{ primary: { key: "title", label: "Titre du point", maxLength: LOT_1_LIMITS.tailored_points.title }, secondary: { key: "detail", label: "Détail", maxLength: LOT_1_LIMITS.tailored_points.detail } }}
        addLabel="Ajouter un point"
      />
      <label className="client-app-editor__field">
        <span>Signature</span>
        <input type="text" maxLength={LOT_1_LIMITS.signature} value={draft.introduction.signature} onChange={(event) => patchIntroduction({ signature: event.target.value })} />
      </label>

      <h5>Stratégie</h5>
      <label className="client-app-editor__field">
        <span>Sous-titre</span>
        <input type="text" maxLength={LOT_1_LIMITS.subtitle} value={draft.strategy.subtitle} onChange={(event) => patchStrategy({ subtitle: event.target.value })} />
      </label>
      <StringListEditor
        legend="Repères essentiels"
        hint={`${LOT_1_LIMITS.essential.min} à ${LOT_1_LIMITS.essential.max} repères, chacun différent. Les retours à la ligne ne sont pas conservés.`}
        values={draft.strategy.essential}
        onChange={(essential) => patchStrategy({ essential })}
        min={LOT_1_LIMITS.essential.min}
        max={LOT_1_LIMITS.essential.max}
        maxLength={LOT_1_LIMITS.essential.maxLength}
        rows={3}
        addLabel="Ajouter un repère"
      />
      <PairListEditor
        legend="Piliers"
        hint={`${LOT_1_LIMITS.pillars.min} à ${LOT_1_LIMITS.pillars.max}`}
        values={draft.strategy.pillars}
        onChange={(pillars) => patchStrategy({ pillars })}
        min={LOT_1_LIMITS.pillars.min}
        max={LOT_1_LIMITS.pillars.max}
        fields={{ primary: { key: "title", label: "Titre du pilier", maxLength: LOT_1_LIMITS.pillars.title }, secondary: { key: "description", label: "Description", maxLength: LOT_1_LIMITS.pillars.description } }}
        addLabel="Ajouter un pilier"
      />
      <StringListEditor
        legend="Points à retenir"
        hint={`${LOT_1_LIMITS.takeaways.min} à ${LOT_1_LIMITS.takeaways.max}, chacun différent`}
        values={draft.strategy.takeaways}
        onChange={(takeaways) => patchStrategy({ takeaways })}
        min={LOT_1_LIMITS.takeaways.min}
        max={LOT_1_LIMITS.takeaways.max}
        maxLength={LOT_1_LIMITS.takeaways.maxLength}
        rows={2}
        addLabel="Ajouter un point à retenir"
      />

      <div className="client-app-editor__actions">
        <button type="button" className="client-app-editor__save" onClick={() => onSubmit(draft)} disabled={disabled || !dirty}>
          {busy ? "Enregistrement…" : "Enregistrer mes corrections"}
        </button>
        <button type="button" className="client-app-editor__reset" onClick={() => setDraft(content)} disabled={!dirty}>
          Annuler mes corrections
        </button>
        <span>{dirty ? "Corrections non enregistrées" : "Texte identique au contenu enregistré"}</span>
      </div>
    </div>
  );
}

export default function ClientAppWorkspace({
  client,
  consultation,
  directives = "",
  onSave,
  onApplied,
  onClose,
  onRegisterSaveAndClose,
  disabled = false,
}) {
  const [activeLot, setActiveLot] = useState("lot_1");
  const [rawByLot, setRawByLot] = useState({});
  const [workspace, setWorkspace] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [error, setError] = useState(null);
  const [mealPrepNotice, setMealPrepNotice] = useState(null);
  const [copyState, setCopyState] = useState("idle");
  const [pasteOpenByLot, setPasteOpenByLot] = useState({});
  const [pendingLot2Import, setPendingLot2Import] = useState(null);
  const [autoState, setAutoState] = useState({});
  const autoRunning = Object.values(autoState).some((entry) => entry?.status === "running" || entry?.status === "generated");
  // Préparation par le cockpit : tous les programmes (mode externe ou plan
  // généré dans le cockpit). Le chemin copier-coller reste disponible.
  const autoPreparation = Boolean(consultation?.id);
  // Statut MGD lu à l'ouverture (fermé tant qu'il n'est pas lu, ou illisible).
  const [mgdState, setMgdState] = useState(null);
  const clientId = client?.id;
  useEffect(() => {
    if (!autoPreparation || !clientId) return undefined;
    let alive = true;
    setMgdState(null);
    readDossierAutoMgdState(clientId).then((state) => { if (alive) setMgdState(state); });
    return () => { alive = false; };
  }, [clientId, autoPreparation]);
  const autoBlocker = useMemo(() => {
    if (!autoPreparation) return null;
    if (mgdState === null) return "Vérification du statut du programme…";
    return getDossierAutoGenerationBlocker({ client, consultation, mgdState });
  }, [client, consultation, autoPreparation, mgdState]);
  const editorIsSource = workspace?.meals_authority?.source === MEALS_AUTHORITY_SOURCES.WEEK_EDITOR;
  // Ce que le dossier appliquera vraiment quand les menus viennent de l'éditeur.
  const weekMenus = useMemo(() => {
    if (!editorIsSource) return null;
    try {
      return buildWeekMenusModel({ client, consultation });
    } catch {
      return null;
    }
  }, [client, consultation, editorIsSource]);
  const weekEditorApplyIssues = useMemo(() => {
    if (!editorIsSource) return [];
    try {
      return getWeekEditorApplyIssues({ client, consultation });
    } catch (caught) {
      return [caught?.message || "Menus de l’éditeur Semaine à vérifier."];
    }
  }, [client, consultation, editorIsSource]);
  const needsMealsSourceChoice = Boolean(workspace)
    && !workspace.meals_authority
    && !workspace.lots?.lot_2
    && hasWeekEditorMeals(consultation);
  const workspaceRef = useRef(null);
  const rawSaveTimerRef = useRef(null);

  const contextState = useMemo(() => {
    try {
      return { context: buildClientAppWorkspaceContext({ client, consultation, directives }), error: null, issues: [] };
    } catch (caught) {
      return {
        context: null,
        error: caught?.message || "Le dossier application ne peut pas être préparé.",
        issues: Array.isArray(caught?.issues) ? caught.issues : [],
      };
    }
  }, [client, consultation, directives]);

  useEffect(() => {
    if (!contextState.context) {
      setWorkspace(null);
      return;
    }
    const existing = consultation?.editorial_overrides?.client_app_workspace || null;
    const resumed = createClientAppWorkspaceDraft(contextState.context, existing);
    workspaceRef.current = resumed;
    setWorkspace(resumed);
    setRawByLot(resumed.raw_by_lot || {});
    setError(null);
  }, [consultation?.editorial_overrides?.client_app_workspace, contextState.context]);

  useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  useEffect(() => () => {
    if (rawSaveTimerRef.current) window.clearTimeout(rawSaveTimerRef.current);
  }, []);

  // Écran fermé pendant une préparation : elle est interrompue avant toute
  // écriture (aucun enregistrement depuis un écran qui n'existe plus).
  const consultationId = consultation?.id;
  useEffect(() => () => abortDossierAutoRun(consultationId), [consultationId]);

  const persist = useCallback(async (nextWorkspace) => {
    if (!contextState.context || typeof onSave !== "function") return null;
    setBusy(true);
    setSaveState("saving");
    setError(null);
    try {
      const updated = await persistClientAppWorkspaceDraft({
        client,
        consultation,
        context: contextState.context,
        workspace: nextWorkspace,
        save: onSave,
      });
      const savedWorkspace = updated?.editorial_overrides?.client_app_workspace || nextWorkspace;
      workspaceRef.current = savedWorkspace;
      setWorkspace(savedWorkspace);
      setSaveState("saved");
      onApplied?.(updated);
      return updated;
    } catch (caught) {
      setSaveState("error");
      setError(caught?.message || "Le brouillon n’a pas pu être enregistré.");
      throw caught;
    } finally {
      setBusy(false);
    }
  }, [client, consultation, contextState.context, onApplied, onSave]);

  const persistRawDraft = useCallback(async (lotId, rawText) => {
    const current = workspaceRef.current;
    if (!current) return current;
    const next = updateClientAppWorkspaceRawDraft(current, lotId, rawText);
    if (next === current) return current;
    workspaceRef.current = next;
    setWorkspace(next);
    return persist(next);
  }, [persist]);

  const queueRawDraft = useCallback((lotId, rawText) => {
    setRawByLot((current) => ({ ...current, [lotId]: rawText }));
    if (rawSaveTimerRef.current) window.clearTimeout(rawSaveTimerRef.current);
    rawSaveTimerRef.current = window.setTimeout(() => {
      rawSaveTimerRef.current = null;
      persistRawDraft(lotId, rawText).catch(() => {});
    }, 900);
  }, [persistRawDraft]);

  const saveAndClose = useCallback(async () => {
    if (busy) return false;
    if (rawSaveTimerRef.current) {
      window.clearTimeout(rawSaveTimerRef.current);
      rawSaveTimerRef.current = null;
    }
    try {
      await persistRawDraft(activeLot, rawByLot[activeLot] || "");
    } catch {
      return false;
    }
    const current = workspaceRef.current;
    if (current?.updated_at && current.status !== "applied" && !busy) {
      try {
        await persist(current);
      } catch {
        return false;
      }
    }
    onClose?.();
    return true;
  }, [activeLot, busy, onClose, persist, persistRawDraft, rawByLot]);

  useEffect(() => {
    if (typeof onRegisterSaveAndClose !== "function") return undefined;
    onRegisterSaveAndClose(saveAndClose);
    return () => onRegisterSaveAndClose(null);
  }, [onRegisterSaveAndClose, saveAndClose]);

  const prompt = useMemo(() => {
    if (!contextState.context) return "";
    try {
      return buildClientAppWorkspaceLotPrompt(contextState.context, activeLot, workspace);
    } catch {
      return "";
    }
  }, [activeLot, contextState.context, workspace]);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1800);
    } catch {
      setCopyState("error");
    }
  };

  const importLot = async (mealsSource) => {
    if (!workspace || !contextState.context) return;
    if (rawSaveTimerRef.current) {
      window.clearTimeout(rawSaveTimerRef.current);
      rawSaveTimerRef.current = null;
    }
    setError(null);
    // Menus de l'éditeur Semaine en place : jamais remplacés sans choix explicite.
    if (activeLot === "lot_2" && editorIsSource && typeof mealsSource !== "string") {
      setPendingLot2Import({ kind: "import" });
      return;
    }
    setPendingLot2Import(null);
    try {
      const next = importClientAppWorkspaceLot({
        workspace,
        context: contextState.context,
        lotId: activeLot,
        rawText: rawByLot[activeLot] || "",
        ...(typeof mealsSource === "string" ? { mealsSource } : {}),
      });
      setWorkspace(next);
      workspaceRef.current = next;
      await persist(next);
      setRawByLot((current) => ({ ...current, [activeLot]: "" }));
      setPasteOpenByLot((current) => ({ ...current, [activeLot]: false }));
      // Lot fait à la main : l'ancien état de la préparation automatique ne vaut plus.
      setAutoState((state) => {
        if (!state[activeLot]) return state;
        const nextState = { ...state };
        delete nextState[activeLot];
        return nextState;
      });
      if (next.lots?.[activeLot]?.status === "needs_anissa_validation") {
        setError(`À valider Anissa — ${next.lots[activeLot].issues.join(" • ")}`);
      }
    } catch (caught) {
      setError(caught?.message || "Ce lot doit être corrigé avant de continuer.");
    }
  };

  const chooseMealsSource = async (source) => {
    if (!workspace) return;
    setError(null);
    try {
      const next = chooseClientAppWorkspaceMealsSource(workspace, consultation, source);
      setWorkspace(next);
      workspaceRef.current = next;
      await persist(next);
    } catch (caught) {
      setError(caught?.message || "Le choix n’a pas pu être enregistré.");
    }
  };

  const editLotContent = async (content, mealsSource) => {
    if (!workspace || !contextState.context) return;
    setError(null);
    // Même confirmation que l'import : corriger le lot Menus et recettes ne
    // remplace jamais en silence les menus de l'éditeur Semaine.
    if (activeLot === "lot_2" && editorIsSource && typeof mealsSource !== "string") {
      setPendingLot2Import({ kind: "edit", content });
      return;
    }
    setPendingLot2Import(null);
    try {
      const next = editClientAppWorkspaceLotContent({
        workspace,
        context: contextState.context,
        lotId: activeLot,
        content,
        ...(typeof mealsSource === "string" ? { mealsSource } : {}),
      });
      if (next === workspace) return;
      setWorkspace(next);
      workspaceRef.current = next;
      await persist(next);
    } catch (caught) {
      setError(caught?.message || "La correction n’a pas pu être enregistrée.");
    }
  };

  const resolvePendingLot2 = (mealsSource) => {
    const pending = pendingLot2Import;
    if (!pending) return;
    if (pending.kind === "edit") editLotContent(pending.content, mealsSource);
    else importLot(mealsSource);
  };

  const goToLot = async (lotId) => {
    if (rawSaveTimerRef.current) {
      window.clearTimeout(rawSaveTimerRef.current);
      rawSaveTimerRef.current = null;
    }
    try {
      await persistRawDraft(activeLot, rawByLot[activeLot] || "");
      setActiveLot(lotId);
      setError(null);
    } catch {
      setError("Le texte collé n’a pas pu être enregistré. Reste sur ce lot puis réessaie.");
    }
  };

  const toggleReview = async (sectionId, reviewed) => {
    if (!workspace) return;
    try {
      const next = markClientAppWorkspaceSectionReviewed(workspace, sectionId, reviewed);
      setWorkspace(next);
      workspaceRef.current = next;
      await persist(next);
    } catch (caught) {
      setError(caught?.message || "La relecture n’a pas pu être enregistrée.");
    }
  };

  // Lots 1 et 2 rédigés par le cockpit, vérifiés comme un lot collé, puis
  // enregistrés en brouillon. Rien n'est appliqué ni validé.
  const runAutoPreparation = async (lots) => {
    const current = workspaceRef.current;
    if (!current || !contextState.context || typeof onSave !== "function" || autoBlocker) return;
    if (isDossierAutoRunning(consultation?.id)) {
      setError(MESSAGE_AUTO_ALREADY_RUNNING);
      return;
    }
    // Un collage en attente d'enregistrement ne doit pas partir pendant la préparation.
    if (rawSaveTimerRef.current) {
      window.clearTimeout(rawSaveTimerRef.current);
      rawSaveTimerRef.current = null;
    }
    setError(null);
    setAutoState((state) => ({
      ...state,
      ...Object.fromEntries(lots.map((lotId) => [lotId, { status: "running" }])),
    }));
    setBusy(true);
    setSaveState("saving");
    try {
      await startDossierAutoRun(consultation?.id, async (signal) => {
      const prepared = await prepareClientAppDossierAuto({
        signal,
        client,
        consultation,
        context: contextState.context,
        workspace: current,
        lots,
        onProgress: (lotId, status) => setAutoState((state) => ({ ...state, [lotId]: { status } })),
        // Relecture en base (même source que l'écran) juste avant l'écriture.
        readFresh: async () => {
          const [{ supabase, isCloudEnabled }, store] = await Promise.all([import("../supabaseClient"), import("../store")]);
          if (!isCloudEnabled) return { cloudDisabled: true };
          return readDossierAutoFreshFromCloud({ client, consultation, db: supabase, buildEntry: store.buildNutritionConsultationEntry });
        },
      });
      const anyReady = Object.values(prepared.results).some((result) => result.ok);
      // Écran fermé entre-temps : rien n'est écrit.
      if (signal.aborted) throw Object.assign(new Error("Préparation interrompue."), { payload: { aborted: true } });
      if (anyReady) {
        const updated = await persistClientAppWorkspaceDraft({
          client,
          consultation: prepared.consultation,
          context: contextState.context,
          workspace: prepared.workspace,
          save: onSave,
        });
        const savedWorkspace = updated?.editorial_overrides?.client_app_workspace || prepared.workspace;
        workspaceRef.current = savedWorkspace;
        setWorkspace(savedWorkspace);
        onApplied?.(updated);
      }
      setSaveState(anyReady ? "saved" : "idle");
      setAutoState((state) => ({
        ...state,
        ...Object.fromEntries(Object.entries(prepared.results).map(([lotId, result]) => [
          lotId,
          result.ok ? { status: "done", revoked: result.revoked || [], warnings: result.warnings || [], completedSlots: result.mode === "complete" ? result.completedSlots : null } : { status: "error", error: result.error, skipped: result.skipped === true },
        ])),
      }));
      });
    } catch (caught) {
      setSaveState("error");
      const message = caught?.payload?.aborted
        ? "La préparation a été interrompue, rien n’a été enregistré. Relance-la."
        : caught?.message || "Le dossier n’a pas pu être préparé. Réessaie.";
      setAutoState((state) => ({
        ...state,
        ...Object.fromEntries(lots.map((lotId) => [lotId, { status: "error", error: message }])),
      }));
    } finally {
      setBusy(false);
    }
  };

  // Lots 3 et 4 sans IA : contenu pré-rempli, importé par le contrôle habituel.
  const importPrefilledLot = async (lotId, rawText) => {
    const current = workspaceRef.current;
    if (!current || !contextState.context) return;
    setError(null);
    try {
      const next = importClientAppWorkspaceLot({ workspace: current, context: contextState.context, lotId, rawText });
      workspaceRef.current = next;
      setWorkspace(next);
      try {
        await persist(next);
      } catch (saveError) {
        // Échec d'enregistrement : le lot n'est pas affiché comme importé.
        workspaceRef.current = current;
        setWorkspace(current);
        throw saveError;
      }
    } catch (caught) {
      setError(caught?.message || "Ce lot n’a pas pu être enregistré.");
    }
  };

  const applyWorkspace = async () => {
    if (!workspace || !contextState.context || typeof onSave !== "function") return;
    setBusy(true);
    setSaveState("saving");
    setError(null);
    try {
      const updated = await applyClientAppWorkspace({
        client,
        consultation,
        context: contextState.context,
        workspace,
        save: onSave,
      });
      setWorkspace(updated.editorial_overrides.client_app_workspace);
      workspaceRef.current = updated.editorial_overrides.client_app_workspace;
      try {
        setMealPrepNotice(mealPrepNotApplied(client, updated));
      } catch {
        setMealPrepNotice(null);
      }
      setSaveState("saved");
      onApplied?.(updated);
    } catch (caught) {
      setSaveState("error");
      setError(caught?.message || "Le dossier n’a pas pu être appliqué.");
    } finally {
      setBusy(false);
    }
  };

  // Menus de l'éditeur Semaine : le lot Menus et recettes n'est plus requis.
  const requiredLots = requiredClientAppWorkspaceLots(workspace);
  const importedCount = requiredLots.filter((lot) => workspace?.lots?.[lot.id]?.status === "ready").length;
  const reviewedCount = CLIENT_APP_WORKSPACE_SECTIONS.filter((section) => workspace?.sections?.[section.id]?.status === "reviewed").length;
  const readyToApply = importedCount === requiredLots.length && reviewedCount === 7 && workspace?.status !== "applied"
    && weekEditorApplyIssues.length === 0;
  const unavailable = disabled || busy || !contextState.context || typeof onSave !== "function";
  const activeLotModel = CLIENT_APP_WORKSPACE_LOTS.find((lot) => lot.id === activeLot);
  const activeLotReady = workspace?.lots?.[activeLot]?.status === "ready";
  const activeLotBlocked = workspace?.lots?.[activeLot]?.status === "needs_anissa_validation";
  // Mode externe : le chemin copier-coller reste disponible, replié par défaut.
  const pasteOpen = pasteOpenByLot[activeLot] ?? (autoPreparation ? false : !activeLotReady);
  const lot2AutoBlocker = useMemo(() => (autoPreparation && contextState.context
    ? getLot2AutoGenerationBlocker({ client, consultation, workspace, context: contextState.context })
    : null), [client, consultation, contextState.context, autoPreparation, workspace]);
  const menusComplete = lot2AutoBlocker === MESSAGE_LOT_2_COMPLETE;
  const autoPendingLots = AUTO_LOT_IDS.filter((lotId) => workspace?.lots?.[lotId]?.status !== "ready"
    && !(lotId === "lot_2" && lot2AutoBlocker));
  // Frigo enregistré qui ne passe plus le contrôle (menus modifiés) : à reconfirmer.
  const storedLot4Issue = autoPreparation && contextState.context
    ? getClientAppWorkspaceLotIssueDetail(contextState.context, workspace, "lot_4")
    : null;
  // Les lots 3 et 4 dépendent des menus : pré-remplis seulement une fois le
  // lot 2 prêt ou l'éditeur Semaine source (sinon le lot 2 les retirerait).
  const menusReady = workspace?.lots?.lot_2?.status === "ready" || editorIsSource;
  const prefillWaitsForMenus = autoPreparation && !menusReady && !activeLotReady
    && (activeLot === "lot_3" || activeLot === "lot_4");
  const prefilledLot = autoPreparation && menusReady && (
    (activeLot === "lot_3" && !activeLotReady)
    || (activeLot === "lot_4" && (!activeLotReady || Boolean(storedLot4Issue))));
  // Pré-remplissage du frigo : contenu, ou la raison pour laquelle il se fait à la main.
  const lot4Prefill = useMemo(() => {
    if (!prefilledLot || activeLot !== "lot_4" || !contextState.context) return null;
    try {
      const rawText = buildLot4RawText(contextState.context, workspace);
      return { rawText, content: JSON.parse(rawText).content, error: null };
    } catch (caught) {
      return { rawText: null, content: null, error: caught?.message || "Le frigo se prépare à la main pour ce programme." };
    }
  }, [activeLot, contextState.context, prefilledLot, workspace]);
  const lot4Preview = lot4Prefill?.content || null;
  const rawText = rawByLot[activeLot] || "";
  const rawReady = rawText.trim().length >= 40;
  const endMarkerSeen = Boolean(contextState.context?.endMarkers?.[activeLot])
    && rawText.includes(contextState.context.endMarkers[activeLot]);
  const activeLotSections = CLIENT_APP_WORKSPACE_SECTIONS.filter((section) => section.lotId === activeLot);
  const activeLotReviewed = activeLotReady
    && activeLotSections.every((section) => workspace?.sections?.[section.id]?.status === "reviewed");
  const activeLotIndex = CLIENT_APP_WORKSPACE_LOTS.findIndex((lot) => lot.id === activeLot);
  // Menus de l'éditeur Semaine : le lot Menus et recettes est sauté.
  const nextLot = CLIENT_APP_WORKSPACE_LOTS.slice(activeLotIndex + 1)
    .find((lot) => requiredLots.some((required) => required.id === lot.id)) || null;
  const nextLotNumber = nextLot ? CLIENT_APP_WORKSPACE_LOTS.indexOf(nextLot) + 1 : null;
  const supplementCount = contextState.context?.supplementItems?.length ?? 0;
  const safetyConfirmations = contextState.context?.safetyConfirmations ?? [];

  return (
    <section className="client-app-workspace" aria-labelledby="client-app-workspace-title">
      <header className="client-app-workspace__hero">
        <div>
          <p>Préparation complète</p>
          <h3 id="client-app-workspace-title">Dossier application</h3>
          <span>Prépare les quatre lots, relis les sept sections, puis applique-les ensemble à Livraison.</span>
        </div>
        <div className="client-app-workspace__hero-actions">
          <span className={`client-app-workspace__save-state is-${saveState}`} role="status">
            {saveState === "saving" ? "Enregistrement…" : saveState === "saved" ? "Brouillon enregistré" : saveState === "error" ? "Échec d’enregistrement" : "Brouillon sauvegardé à chaque étape"}
          </span>
        </div>
      </header>

      <div className="client-app-workspace__progress" aria-label={`${reviewedCount} sections relues sur 7`}>
        <div><span style={{ width: `${(reviewedCount / 7) * 100}%` }} /></div>
        <strong>{importedCount}/4 lots importés</strong>
        <strong>{reviewedCount}/7 sections relues</strong>
      </div>

      {autoPreparation && contextState.context && (
        <div className="client-app-workspace__confirmations client-app-workspace__auto" role="region" aria-label="Préparation automatique du dossier">
          <strong>Préparer le dossier de l’app</strong>
          {autoBlocker ? (
            <span role="alert">{autoBlocker}</span>
          ) : (
            <span>
              Le cockpit rédige l’introduction, la stratégie, les menus et les recettes à partir du programme que tu as validé.
              Rien n’est appliqué : tu relis chaque section, et chaque repas se valide dans « Semaine, menus & recettes ».
              Les réglages du Meal Prep (lot 3) et le frigo et les compléments (lot 4) sont pré-remplis, tu les confirmes dans leur onglet.
            </span>
          )}
          {!autoBlocker && (
            <div className="client-app-workspace__actions">
              <button
                type="button"
                onClick={() => runAutoPreparation(autoPendingLots)}
                disabled={unavailable || autoRunning || autoPendingLots.length === 0}
              >
                {autoRunning ? "Préparation en cours, 1 à 3 minutes…" : autoPendingLots.length === 0 ? (menusComplete ? "Menus déjà complets" : "Lots 1 et 2 préparés") : "Préparer le dossier de l’app"}
              </button>
            </div>
          )}
          {lot2AutoBlocker && !autoBlocker && workspace?.lots?.lot_2?.status !== "ready" && <span>{lot2AutoBlocker}</span>}
          {AUTO_LOT_IDS.filter((lotId) => autoState[lotId]).map((lotId) => {
            const entry = autoState[lotId];
            const label = CLIENT_APP_WORKSPACE_LOTS.find((lot) => lot.id === lotId)?.label;
            return (
              <p key={lotId} className={entry.status === "error" ? "client-app-workspace__inline-error" : undefined} role="status">
                <strong>{label}</strong> · {entry.status === "error" ? entry.error : AUTO_STATUS_LABELS[entry.status]}
                {entry.revoked?.length > 0 && " · Les lots 3 et 4 dépendent des menus : reconfirme-les dans leur onglet."}
                {entry.completedSlots != null && (entry.completedSlots > 0
                  ? ` · ${entry.completedSlots} repas complété${entry.completedSlots > 1 ? "s" : ""}, à revalider dans « Semaine, menus & recettes »`
                  : " · Menus déjà complets, rien n’a été généré")}
                {entry.warnings?.map((warning) => <span key={warning} className="client-app-workspace__auto-warning"><br />{warning}</span>)}
                {entry.status === "error" && !entry.skipped && workspace?.lots?.[lotId]?.status !== "ready" && (
                  <>
                    {" "}
                    <button type="button" onClick={() => runAutoPreparation([lotId])} disabled={unavailable || autoRunning}>
                      Réessayer ce lot
                    </button>
                  </>
                )}
              </p>
            );
          })}
        </div>
      )}

      {safetyConfirmations.length > 0 && (
        <div className="client-app-workspace__confirmations" role="status">
          <strong>{safetyConfirmations.length} point{safetyConfirmations.length > 1 ? "s" : ""} à confirmer dans l’anamnèse</strong>
          <span>Rien n’est bloqué. Tu peux préparer le dossier et revenir sur ces points quand tu veux.</span>
          <ul>
            {safetyConfirmations.map((issue) => (
              <li key={issue.field}><strong>{issue.label}</strong> · {issue.message.replace(/^À confirmer — /, "")}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="client-app-workspace__body">
        <aside className="client-app-workspace__lots" aria-label="Lots du dossier">
          {CLIENT_APP_WORKSPACE_LOTS.map((lot, index) => {
            const imported = workspace?.lots?.[lot.id]?.status === "ready";
            const blocked = workspace?.lots?.[lot.id]?.status === "needs_anissa_validation";
            return (
              <button
                type="button"
                key={lot.id}
                className={`${activeLot === lot.id ? "is-active" : ""} ${imported ? "is-imported" : ""}`}
                onClick={() => goToLot(lot.id)}
              >
                <span>{index + 1}</span>
                <span><strong>{lot.label}</strong><small>{imported ? "Importé · à relire" : blocked ? "À valider Anissa" : LOT_HELP[lot.id]}</small></span>
                {imported && <Icon type="check" size={14} />}
              </button>
            );
          })}
        </aside>

        <div className="client-app-workspace__import">
          <div className="client-app-workspace__panel-heading">
            <div><p>Lot {activeLot.slice(-1)} sur 4</p><h4>{activeLotModel?.label}</h4></div>
            <span className={activeLotReady ? "is-ready" : undefined}>
              {activeLotReady ? "Contenu enregistré" : activeLotBlocked ? "À valider Anissa" : "À préparer"}
            </span>
          </div>

          {activeLot === "lot_2" && (
            <p className="client-app-workspace__notice" role="status">
              <strong>Les menus et les quantités sont des propositions.</strong>
              {" "}Quand le programme ne les détaille pas, ils sont proposés pour te faire gagner du temps,
              puis c’est toi qui les relis et les valides. Aucun complément ni dosage n’est jamais proposé.
            </p>
          )}

          {/* Une section Compléments vide est un message fort pour la cliente.
              Elle doit être un choix conscient d'Anissa, jamais un silence. */}
          {activeLot === "lot_4" && contextState.context && (
            supplementCount === 0 ? (
              <p className="client-app-workspace__notice is-warn" role="status">
                <strong>Aucun complément reconnu dans ce programme.</strong>
                {" "}L’application indiquera à {client?.prenom || "la cliente"} qu’elle n’a pas de compléments.
                Si son programme en prescrit, corrige leur mise en forme dans le plan avant de continuer.
              </p>
            ) : (
              <p className="client-app-workspace__notice" role="status">
                <strong>{supplementCount} complément{supplementCount > 1 ? "s" : ""} repris du programme validé.</strong>
                {" "}Ils ne peuvent être ni modifiés ni complétés ici.
              </p>
            )
          )}

          {prefillWaitsForMenus && (
            <p className="client-app-workspace__notice" role="status">
              Prépare d’abord les menus (lot 2) : ce lot en dépend et serait à refaire après eux.
            </p>
          )}

          {prefilledLot && activeLot === "lot_3" && (
            <section className="client-app-workspace__step is-open">
              <div className="client-app-workspace__step-title">
                <strong>Étape 1 · Confirmer les réglages</strong>
                <small>Pré-remplis, à ajuster si besoin</small>
              </div>
              <Lot3Form
                disabled={unavailable}
                onSubmit={(settings) => importPrefilledLot("lot_3", buildLot3RawText(contextState.context, workspace, settings))}
              />
            </section>
          )}

          {prefilledLot && activeLot === "lot_4" && (
            <section className="client-app-workspace__step is-open">
              <div className="client-app-workspace__step-title">
                <strong>Étape 1 · Confirmer le frigo et les compléments</strong>
                <small>Repris tels quels des repères du programme et du protocole validé</small>
              </div>
              {storedLot4Issue && (
                <p className="client-app-workspace__notice is-warn" role="alert">
                  <strong>{storedLot4Issue.code === "ai_mention" ? "Le frigo contient une mention d’outil ou d’IA." : "Le frigo doit être reconfirmé (les menus ont changé)."}</strong> {storedLot4Issue.message}
                </p>
              )}
              {lot4Prefill?.error && (
                <p className="client-app-workspace__notice is-warn" role="alert">{lot4Prefill.error}</p>
              )}
              {lot4Preview && (
                <div className="client-app-workspace__lot-preview">
                  <p><strong>À privilégier</strong> · {lot4Preview.fridge.to_favor.join(" · ") || "Aucun repère disponible"}</p>
                  <p><strong>Compléments</strong> · {lot4Preview.supplements.intro}</p>
                </div>
              )}
              <div className="client-app-editor__actions">
                <button
                  type="button"
                  className="client-app-editor__save"
                  onClick={() => importPrefilledLot("lot_4", lot4Prefill?.rawText)}
                  disabled={unavailable || !lot4Prefill?.rawText}
                >
                  Confirmer le frigo et les compléments
                </button>
              </div>
            </section>
          )}

          {autoPreparation && !activeLotReady && (activeLot === "lot_1" || activeLot === "lot_2") && !autoBlocker && (
            <p className="client-app-workspace__notice" role="status">
              Ce lot se prépare avec le bouton « Préparer le dossier de l’app » en haut. Le passage par copier-coller reste possible ci-dessous.
            </p>
          )}

          <section className={`client-app-workspace__step${pasteOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="client-app-workspace__step-toggle"
              onClick={() => setPasteOpenByLot((current) => ({ ...current, [activeLot]: !pasteOpen }))}
              aria-expanded={pasteOpen}
            >
              {autoPreparation ? (
                <span><strong>Faire à la main à la place</strong><small>Copier le cadre, coller la réponse : l’ancien passage, toujours disponible</small></span>
              ) : (
                <span><strong>Étape 1 · Préparer et coller</strong><small>{activeLotReady ? "Terminée, à rouvrir seulement pour recommencer ce lot" : "Le passage obligé, une seule fois par lot"}</small></span>
              )}
              <span className="client-app-workspace__step-chevron">{pasteOpen ? "Masquer" : "Ouvrir"}</span>
            </button>
            {pasteOpen && (
              <div className="client-app-workspace__step-body">
                <ol className="client-app-workspace__instructions">
                  <li>Copie le cadre de préparation de ce lot.</li>
                  <li>Prépare le contenu dans la conversation de la cliente.</li>
                  <li>Colle la réponse complète ici puis vérifie-la.</li>
                </ol>
                <button type="button" className="client-app-workspace__copy" onClick={copyPrompt} disabled={unavailable || !prompt}>
                  {copyState === "copied" ? "Cadre copié" : "Copier le cadre de préparation"}
                </button>
                {copyState === "error" && <p className="client-app-workspace__inline-error">La copie est indisponible. Recharge puis réessaie.</p>}
                <label htmlFor={`client-app-workspace-${activeLot}`}>
                  Zone de collage <small>colle simplement ici, tu n’as rien à lire ni à corriger dans cette zone</small>
                </label>
                {rawReady ? (
                  <div className="client-app-workspace__pasted" role="status">
                    <strong>Réponse reçue</strong>
                    <span>{rawText.trim().length.toLocaleString("fr-CH")} signes</span>
                    {looksLikePastedFrame(rawText) ? (
                      <span className="is-warn" role="alert">{PASTED_FRAME_MESSAGE}</span>
                    ) : (
                      <span className={endMarkerSeen ? "is-ok" : "is-warn"}>
                        {endMarkerSeen ? "Fin de réponse détectée" : "Fin de réponse absente, la réponse est peut-être coupée"}
                      </span>
                    )}
                    <button
                      type="button"
                      className="client-app-workspace__clear"
                      onClick={() => { queueRawDraft(activeLot, ""); setError(null); }}
                      disabled={unavailable}
                    >
                      Effacer et recoller
                    </button>
                  </div>
                ) : (
                  <textarea
                    id={`client-app-workspace-${activeLot}`}
                    value={rawText}
                    onChange={(event) => { queueRawDraft(activeLot, event.target.value); setError(null); }}
                    rows={6}
                    spellCheck="false"
                    placeholder="Colle ici la réponse complète du lot."
                    disabled={unavailable}
                  />
                )}
                <button
                  type="button"
                  className="client-app-workspace__verify"
                  onClick={importLot}
                  disabled={unavailable || !rawReady}
                >
                  {activeLotReady ? "Vérifier et remplacer ce lot" : "Vérifier et enregistrer ce lot"}
                </button>
              </div>
            )}
          </section>

          {activeLotBlocked && <LotPreview lotId={activeLot} workspace={workspace} context={contextState.context} weekMenus={weekMenus} />}

          {activeLotReady && (
            <section className="client-app-workspace__step is-open client-app-workspace__step--review">
              <div className="client-app-workspace__step-title">
                <strong>Étape 2 · Relire et corriger</strong>
                <small>{activeLot === "lot_1" ? "Modifiable mot à mot" : "Lecture seule, les quantités viennent du programme validé"}</small>
              </div>
              {activeLot === "lot_1" && Array.isArray(workspace.lots.lot_1.review_notes) && workspace.lots.lot_1.review_notes.length > 0 && (
                <ul className="client-app-workspace__notice is-warn" role="status">
                  {workspace.lots.lot_1.review_notes.map((note) => <li key={note}>{note}</li>)}
                </ul>
              )}
              {activeLot === "lot_1" ? (
                <Lot1Editor
                  content={workspace.lots.lot_1.content}
                  onSubmit={editLotContent}
                  disabled={unavailable}
                  busy={busy}
                />
              ) : (
                <LotPreview lotId={activeLot} workspace={workspace} context={contextState.context} weekMenus={weekMenus} />
              )}

              <div className="client-app-workspace__lot-review">
                {activeLotSections.map((section) => {
                  const reviewed = workspace?.sections?.[section.id]?.status === "reviewed";
                  return (
                    <label key={section.id} className={reviewed ? "is-reviewed" : undefined}>
                      <input
                        type="checkbox"
                        checked={reviewed}
                        onChange={(event) => toggleReview(section.id, event.target.checked)}
                        disabled={unavailable}
                      />
                      <span>J’ai relu <strong>{section.label}</strong></span>
                    </label>
                  );
                })}
                {activeLotReviewed && nextLot && (
                  <button type="button" className="client-app-workspace__next-lot" onClick={() => goToLot(nextLot.id)} disabled={busy}>
                    {activeLotModel?.label} terminé · Passer au lot {nextLotNumber} →
                  </button>
                )}
                {activeLotReviewed && !nextLot && (
                  <p className="client-app-workspace__next-lot-done">
                    {reviewedCount === 7 && importedCount === requiredLots.length
                      ? `${requiredLots.length === CLIENT_APP_WORKSPACE_LOTS.length ? "Les quatre lots sont relus" : "Les trois lots nécessaires sont relus (menus de l’éditeur Semaine)"}. L’application est en bas de page.`
                      : "Ce lot est relu. Reprends les lots encore incomplets à gauche."}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>

        <aside className="client-app-workspace__review" aria-label="Relecture des sections">
          <div className="client-app-workspace__panel-heading">
            <div><p>Relecture Anissa</p><h4>7 sections</h4></div>
          </div>
          <div className="client-app-workspace__review-list">
            {CLIENT_APP_WORKSPACE_SECTIONS.map((section, index) => {
              const state = workspace?.sections?.[section.id];
              const available = workspace?.lots?.[section.lotId]?.status === "ready"
                || (section.id === "week" && editorIsSource);
              const reviewed = state?.status === "reviewed";
              return (
                <label key={section.id} className={`${available ? "" : "is-disabled"} ${reviewed ? "is-reviewed" : ""}`}>
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={(event) => toggleReview(section.id, event.target.checked)}
                    disabled={!available || unavailable}
                  />
                  <span className="client-app-workspace__review-index">{String(index + 1).padStart(2, "0")}</span>
                  <span><strong>{section.label}</strong><small>{sectionPreview(section.id, workspace, weekMenus)}</small></span>
                </label>
              );
            })}
          </div>
        </aside>
      </div>

      {(contextState.error || error) && (
        <div className="client-app-workspace__error" role="alert">
          <strong>À corriger avant de continuer</strong>
          <span>{error || contextState.error}</span>
          {contextState.issues.length > 0 && (
            <>
              <p>Complète ces champs puis sauvegarde l’anamnèse une seule fois :</p>
              <ul>
                {contextState.issues.map((issue) => (
                  <li key={issue.field}><strong>{issue.label}</strong> · {issue.section}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {needsMealsSourceChoice && (
        <div className="client-app-workspace__confirmations" role="alertdialog" aria-labelledby="client-app-workspace-meals-source">
          <strong id="client-app-workspace-meals-source">Des menus ont déjà été modifiés dans l’éditeur Semaine. Quelle source veux-tu appliquer ?</strong>
          <p>Les menus de l’éditeur seront appliqués tels quels ; le lot Menus et recettes les remplacerait à l’application.</p>
          <div className="client-app-workspace__actions">
            <button type="button" onClick={() => chooseMealsSource(MEALS_AUTHORITY_SOURCES.WEEK_EDITOR)} disabled={unavailable}>
              Garder les menus de l’éditeur
            </button>
            <button type="button" onClick={() => chooseMealsSource(MEALS_AUTHORITY_SOURCES.LOT_2)} disabled={unavailable}>
              Utiliser le lot Menus et recettes
            </button>
          </div>
        </div>
      )}

      {pendingLot2Import && (
        <div className="client-app-workspace__confirmations" role="alertdialog" aria-labelledby="client-app-workspace-lot2-confirm">
          <strong id="client-app-workspace-lot2-confirm">Les menus actifs viennent de l’éditeur Semaine.</strong>
          <p>{pendingLot2Import.kind === "edit" ? "Corriger" : "Importer"} ce lot peut les remplacer à l’application. Choisis explicitement :</p>
          <div className="client-app-workspace__actions">
            <button type="button" onClick={() => resolvePendingLot2(MEALS_AUTHORITY_SOURCES.WEEK_EDITOR)} disabled={unavailable}>
              {pendingLot2Import.kind === "edit" ? "Corriger le lot" : "Importer le lot"} et garder les menus de l’éditeur
            </button>
            <button type="button" onClick={() => resolvePendingLot2(MEALS_AUTHORITY_SOURCES.LOT_2)} disabled={unavailable}>
              Remplacer par les menus du lot
            </button>
            <button type="button" onClick={() => setPendingLot2Import(null)} disabled={busy}>Annuler</button>
          </div>
        </div>
      )}

      {editorIsSource && weekEditorApplyIssues.length > 0 && (
        <div className="client-app-workspace__error" role="status">
          <strong>{WEEK_EDITOR_SOURCE_LINE} — à régler avant l’application</strong>
          <ul>{weekEditorApplyIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
        </div>
      )}

      {mealPrepNotice && (
        <p className="client-app-workspace__notice is-warn" role="status">
          <strong>Dossier appliqué, sans Meal Prep pour l’instant.</strong> L’app de {client?.prenom || "la cliente"} ne l’affichera pas. Raison : {mealPrepNotice}. Pour l’ajouter plus tard, corrige la recette concernée dans « Semaine, menus & recettes » puis valide le Meal Prep.
        </p>
      )}

      <footer className="client-app-workspace__footer">
        <div>
          <strong>{workspace?.status === "applied" ? "Dossier appliqué à Livraison" : readyToApply ? "Dossier prêt à appliquer" : "Préparation en cours"}</strong>
          <span>{workspace?.status === "applied" ? "La publication reste une étape séparée." : "Aucun contenu n’est publié pendant la préparation."}</span>
        </div>
        <button type="button" onClick={applyWorkspace} disabled={!readyToApply || unavailable}>
          {busy ? "Application…" : workspace?.status === "applied" ? "Déjà appliqué" : "Appliquer les 7 sections à Livraison"}
        </button>
      </footer>
    </section>
  );
}
